import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Download,
  History,
  Plus,
  Search,
  SlidersHorizontal,
  Users,
  X,
} from "lucide-react";

import { AuditTrailDialog } from "@/components/audit/audit-trail-panel";
import { CreateEmployeeDialog } from "@/components/create-employee";
import { EmptyState } from "@/components/empty-state";
import { PageHeader } from "@/components/shell/page-header";
import { Avatar } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  useColumns,
  type ColumnOption,
} from "@/components/ui/columns-picker";
import {
  DataTable,
  type DataTableColumn,
  type DataTableSort,
} from "@/components/ui/data-table";
import { ErrorMessage } from "@/components/ui/error-message";
import { FilterField } from "@/components/ui/filter-field";
import {
  PaginationFooter,
  usePagination,
} from "@/components/ui/pagination-footer";
import { Select } from "@/components/ui/select";
import {
  StatusPill,
  employeeStatusTone,
  humanizeStatus,
} from "@/components/ui/status-pill";
import { Tooltip } from "@/components/ui/tooltip";
import { useToast } from "@/components/ui/toast";
import { api, type Paginated } from "@/lib/api";
import { formatDate, formatPHP } from "@/lib/format";
import type { Employee } from "@/lib/types";
import { cn } from "@/lib/utils";

/* ------------------------------------------------------------------ */
/* Column model — togglable, persisted per browser                      */
/* ------------------------------------------------------------------ */

type ColumnId =
  | "employee_id"
  | "name"
  | "status"
  | "department"
  | "designation"
  | "branch"
  | "employment_type"
  | "joined"
  | "tenure"
  | "monthly_basic";

const COLUMN_OPTIONS: ReadonlyArray<ColumnOption & { id: ColumnId }> = [
  { id: "employee_id", label: "Employee ID" },
  { id: "name", label: "Name", locked: true },
  { id: "status", label: "Status" },
  { id: "department", label: "Department" },
  { id: "designation", label: "Designation", defaultVisible: false },
  { id: "branch", label: "Branch", defaultVisible: false },
  { id: "employment_type", label: "Employment type", defaultVisible: false },
  { id: "joined", label: "Joined" },
  { id: "tenure", label: "Tenure", defaultVisible: false },
  // Compensation data is opt-in, never on by default.
  { id: "monthly_basic", label: "Monthly basic", defaultVisible: false },
];

/* ------------------------------------------------------------------ */
/* Filters                                                              */
/* ------------------------------------------------------------------ */

interface Filters {
  status: string;
  department: string;
  designation: string;
  branch: string;
  employmentType: string;
  joinedFrom: string;
  joinedTo: string;
}

const EMPTY_FILTERS: Filters = {
  status: "",
  department: "",
  designation: "",
  branch: "",
  employmentType: "",
  joinedFrom: "",
  joinedTo: "",
};

const STATUS_OPTIONS = [
  "active",
  "probationary",
  "regular",
  "on_leave",
  "suspended",
  "awol",
  "resigned",
  "terminated",
  "end_of_contract",
  "retired",
  "deceased",
];

interface CatalogRow {
  id: string;
  name: string;
}

function useCatalog(resource: string) {
  return useQuery({
    queryKey: ["org-catalog", resource],
    queryFn: () =>
      api.get<Paginated<CatalogRow>>(
        `/organization/${resource}/?page_size=200&ordering=name`,
      ),
    staleTime: 5 * 60 * 1000,
  });
}

/* ------------------------------------------------------------------ */
/* Page                                                                 */
/* ------------------------------------------------------------------ */

export function EmployeesPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const [createOpen, setCreateOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<DataTableSort | undefined>({
    key: "date_of_joining",
    direction: "desc",
  });
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const { visible: visibleColumns, picker: columnsPicker } = useColumns(
    "employees.columns",
    COLUMN_OPTIONS,
  );
  const [exporting, setExporting] = useState(false);
  const [auditRow, setAuditRow] = useState<Employee | null>(null);

  // Any change to what's being asked for goes back to page 1.
  const pager = usePagination(JSON.stringify({ query, filters, sort }));

  const departments = useCatalog("departments");
  const designations = useCatalog("designations");
  const branches = useCatalog("branches");
  const employmentTypes = useCatalog("employment-types");

  const nameOf = useMemo(() => {
    const m = new Map<string, string>();
    for (const cat of [departments, designations, branches, employmentTypes]) {
      for (const row of cat.data?.results ?? []) m.set(row.id, row.name);
    }
    return m;
  }, [departments.data, designations.data, branches.data, employmentTypes.data]);

  function listParams(): URLSearchParams {
    const params = new URLSearchParams();
    if (query.trim()) params.set("search", query.trim());
    if (sort) {
      params.set(
        "ordering",
        `${sort.direction === "desc" ? "-" : ""}${sort.key}`,
      );
    }
    if (filters.status) params.set("status", filters.status);
    if (filters.department) params.set("department", filters.department);
    if (filters.designation) params.set("designation", filters.designation);
    if (filters.branch) params.set("branch", filters.branch);
    if (filters.employmentType)
      params.set("employment_type", filters.employmentType);
    if (filters.joinedFrom)
      params.set("date_of_joining__gte", filters.joinedFrom);
    if (filters.joinedTo) params.set("date_of_joining__lte", filters.joinedTo);
    return params;
  }

  const employees = useQuery({
    queryKey: [
      "employees", "list", query, sort, filters, pager.page, pager.pageSize,
    ],
    queryFn: () => {
      const params = listParams();
      params.set("page", String(pager.page));
      params.set("page_size", String(pager.pageSize));
      return api.get<Paginated<Employee>>(
        `/employees/employees/?${params.toString()}`,
      );
    },
    placeholderData: (prev) => prev,
  });

  const rows = useMemo(() => employees.data?.results ?? [], [employees.data]);
  const totalCount = employees.data?.count;
  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  async function handleExport() {
    setExporting(true);
    try {
      const params = listParams();
      if (visibleColumns.includes("monthly_basic")) {
        params.set("include_salary", "true");
      }
      const blob = await api.blob(
        `/employees/employees/export/?${params.toString()}`,
      );
      const stamp = new Date().toISOString().slice(0, 10);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `employees-${stamp}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 0);
    } catch (err) {
      toast.error(
        "Export failed",
        err instanceof Error ? err.message : undefined,
      );
    } finally {
      setExporting(false);
    }
  }

  /* ----- columns ----- */

  const allColumns: Record<ColumnId, DataTableColumn<Employee>> = useMemo(
    () => ({
      employee_id: {
        key: "employee_id",
        header: "Employee ID",
        sortKey: "employee_id",
        width: "min",
        cell: (e) => (
          <Link
            to={`/employees/${e.id}`}
            className="font-mono text-sm text-navy hover:text-blue hover:underline underline-offset-4"
          >
            {e.employee_id}
          </Link>
        ),
      },
      name: {
        key: "name",
        header: "Name",
        sortKey: "last_name",
        cell: (e) => (
          <div className="flex items-center gap-3">
            <Avatar
              name={e.full_name}
              photo={e.photo}
              fallback={e.employee_id}
              size={32}
            />
            <div className="min-w-0">
              <Link
                to={`/employees/${e.id}`}
                className="block truncate text-sm font-medium text-navy hover:text-blue hover:underline underline-offset-4"
              >
                {e.full_name}
              </Link>
              <p className="truncate text-xs text-slate">
                {e.company_email || e.personal_email || "—"}
              </p>
            </div>
          </div>
        ),
      },
      status: {
        key: "status",
        header: "Status",
        sortKey: "status",
        cell: (e) => (
          <StatusPill tone={employeeStatusTone(e.status)}>
            {humanizeStatus(e.status)}
          </StatusPill>
        ),
      },
      department: {
        key: "department",
        header: "Department",
        cell: (e) => (
          <span className="text-sm text-foreground">
            {(e.department && nameOf.get(e.department)) || "—"}
          </span>
        ),
      },
      designation: {
        key: "designation",
        header: "Designation",
        cell: (e) => (
          <span className="text-sm text-foreground">
            {(e.designation && nameOf.get(e.designation)) || "—"}
          </span>
        ),
      },
      branch: {
        key: "branch",
        header: "Branch",
        cell: (e) => (
          <span className="text-sm text-foreground">
            {(e.branch && nameOf.get(e.branch)) || "—"}
          </span>
        ),
      },
      employment_type: {
        key: "employment_type",
        header: "Employment type",
        cell: (e) => (
          <span className="text-sm text-foreground">
            {(e.employment_type && nameOf.get(e.employment_type)) || "—"}
          </span>
        ),
      },
      joined: {
        key: "joined",
        header: "Joined",
        sortKey: "date_of_joining",
        cell: (e) => (
          <span className="text-sm tabular-nums text-foreground">
            {formatDate(e.date_of_joining)}
          </span>
        ),
      },
      tenure: {
        key: "tenure",
        header: "Tenure",
        numeric: true,
        cell: (e) => (
          <span className="text-sm tabular-nums">
            {e.years_of_service != null
              ? `${e.years_of_service.toFixed(1)} yr${e.years_of_service === 1 ? "" : "s"}`
              : "—"}
          </span>
        ),
      },
      monthly_basic: {
        key: "monthly_basic",
        header: "Monthly basic",
        numeric: true,
        sortKey: "monthly_basic",
        cell: (e) => (
          <span className="text-sm">
            {e.monthly_basic ? formatPHP(e.monthly_basic) : "—"}
          </span>
        ),
      },
    }),
    [nameOf],
  );

  const columns: DataTableColumn<Employee>[] = useMemo(() => {
    const ordered = COLUMN_OPTIONS.filter((c) =>
      visibleColumns.includes(c.id),
    ).map((c) => allColumns[c.id]);
    ordered.push({
      key: "actions",
      header: "",
      actions: true,
      width: "min",
      cell: (e) => (
        <Tooltip label="View audit trail">
          <button
            type="button"
            onClick={() => setAuditRow(e)}
            className="rounded p-1 text-slate hover:bg-slate-soft hover:text-navy"
            aria-label={`View audit trail for ${e.full_name}`}
          >
            <History size={15} aria-hidden="true" />
          </button>
        </Tooltip>
      ),
    });
    return ordered;
  }, [allColumns, visibleColumns]);

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Employees"
        subtitle={
          totalCount !== undefined
            ? `${totalCount.toLocaleString("en-PH")} ${totalCount === 1 ? "person" : "people"}`
            : "Loading roster…"
        }
        actions={
          <>
            <Button
              variant="outline"
              size="sm"
              onClick={handleExport}
              disabled={exporting || employees.isLoading}
            >
              <Download size={16} strokeWidth={1.5} aria-hidden="true" />
              {exporting ? "Exporting…" : "Export"}
            </Button>
            <Button size="sm" onClick={() => setCreateOpen(true)}>
              <Plus size={16} strokeWidth={1.5} aria-hidden="true" />
              Add employee
            </Button>
          </>
        }
      />

      <CreateEmployeeDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
      />

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative w-full max-w-sm">
          <Search
            size={16}
            strokeWidth={1.5}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate"
            aria-hidden="true"
          />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name, ID, email…"
            aria-label="Search employees"
            className="h-10 w-full rounded-btn border border-slate-200 bg-white pl-9 pr-3 text-sm text-foreground placeholder:text-slate-400 focus:border-blue focus:outline-none focus-visible:ring-2 focus-visible:ring-blue/20"
          />
        </div>

        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setFiltersOpen((v) => !v)}
          aria-expanded={filtersOpen}
          className={cn("gap-1.5", activeFilterCount > 0 && "border-blue text-blue")}
        >
          <SlidersHorizontal size={15} strokeWidth={1.5} aria-hidden="true" />
          Filters
          {activeFilterCount > 0 && (
            <span className="rounded-full bg-blue px-1.5 text-[11px] font-semibold leading-4 text-white">
              {activeFilterCount}
            </span>
          )}
        </Button>

        {columnsPicker}
      </div>

      {/* Filter panel */}
      {filtersOpen && (
        <div className="rounded-card border border-slate-200 bg-white p-4">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <FilterField label="Status">
              <Select
                value={filters.status}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, status: e.target.value }))
                }
                aria-label="Filter by status"
              >
                <option value="">All statuses</option>
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {humanizeStatus(s)}
                  </option>
                ))}
              </Select>
            </FilterField>
            <FilterField label="Department">
              <CatalogSelect
                rows={departments.data?.results}
                value={filters.department}
                placeholder="All departments"
                onChange={(v) => setFilters((f) => ({ ...f, department: v }))}
              />
            </FilterField>
            <FilterField label="Designation">
              <CatalogSelect
                rows={designations.data?.results}
                value={filters.designation}
                placeholder="All designations"
                onChange={(v) => setFilters((f) => ({ ...f, designation: v }))}
              />
            </FilterField>
            <FilterField label="Branch">
              <CatalogSelect
                rows={branches.data?.results}
                value={filters.branch}
                placeholder="All branches"
                onChange={(v) => setFilters((f) => ({ ...f, branch: v }))}
              />
            </FilterField>
            <FilterField label="Employment type">
              <CatalogSelect
                rows={employmentTypes.data?.results}
                value={filters.employmentType}
                placeholder="All types"
                onChange={(v) =>
                  setFilters((f) => ({ ...f, employmentType: v }))
                }
              />
            </FilterField>
            <FilterField label="Joined from">
              <input
                type="date"
                value={filters.joinedFrom}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, joinedFrom: e.target.value }))
                }
                aria-label="Joined on or after"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
            </FilterField>
            <FilterField label="Joined to">
              <input
                type="date"
                value={filters.joinedTo}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, joinedTo: e.target.value }))
                }
                aria-label="Joined on or before"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
            </FilterField>
            <div className="flex items-end">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={activeFilterCount === 0}
                onClick={() => setFilters(EMPTY_FILTERS)}
                className="gap-1 text-slate"
              >
                <X size={14} aria-hidden="true" />
                Clear filters
              </Button>
            </div>
          </div>
        </div>
      )}

      {employees.isError && (
        <ErrorMessage
          error={employees.error}
          fallback="Failed to load employees."
        />
      )}

      <DataTable<Employee>
        columns={columns}
        rows={rows}
        rowKey={(e) => e.id}
        loading={employees.isLoading}
        skeletonRows={8}
        sort={sort}
        onSortChange={setSort}
        onRowDoubleClick={(e) => navigate(`/employees/${e.id}`)}
        emptyState={
          <EmptyState
            icon={Users}
            title={
              activeFilterCount > 0 || query
                ? "No employees match"
                : "No employees yet"
            }
            description={
              activeFilterCount > 0 || query
                ? "Try widening the search or clearing some filters."
                : "Add the first employee to get started, or run `make seed` to populate the demo company."
            }
            action={
              activeFilterCount > 0 || query ? (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setFilters(EMPTY_FILTERS);
                    setQuery("");
                  }}
                >
                  Clear search & filters
                </Button>
              ) : (
                <Button size="sm" onClick={() => setCreateOpen(true)}>
                  <Plus size={16} strokeWidth={1.5} aria-hidden="true" />
                  Add employee
                </Button>
              )
            }
          />
        }
      />

      <PaginationFooter
        pager={pager}
        count={totalCount}
        hasNext={!!employees.data?.next}
        isFetching={employees.isFetching}
      />

      {auditRow && (
        <AuditTrailDialog
          open
          onClose={() => setAuditRow(null)}
          title={`Audit trail — ${auditRow.full_name}`}
          resourcePath="employees/employees"
          recordId={auditRow.id}
          recordNoun="employee"
          entityType="employees.Employee"
          entityLabel={auditRow.full_name}
        />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Bits                                                                 */
/* ------------------------------------------------------------------ */

function CatalogSelect({
  rows,
  value,
  placeholder,
  onChange,
}: {
  rows: CatalogRow[] | undefined;
  value: string;
  placeholder: string;
  onChange: (value: string) => void;
}) {
  return (
    <Select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      aria-label={placeholder}
    >
      <option value="">{placeholder}</option>
      {(rows ?? []).map((r) => (
        <option key={r.id} value={r.id}>
          {r.name}
        </option>
      ))}
    </Select>
  );
}
